import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Building2, School, Heart, Cpu, HardHat, ArrowRight, MapPin, Calendar, Monitor, Stethoscope, Wifi, Shield, Users, GraduationCap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

const categories = [
  { id: 'all', name: 'All Projects', icon: Building2 },
  { id: 'education', name: 'Education', icon: School },
  { id: 'healthcare', name: 'Healthcare', icon: Heart },
  { id: 'technology', name: 'Technology', icon: Cpu },
  { id: 'infrastructure', name: 'Infrastructure', icon: HardHat },
  { id: 'government', name: 'Government', icon: Building2 },
]

const projects = [
  {
    id: 1,
    title: 'Smart Classroom Project - Govt. College Pune',
    category: 'education',
    client: 'Government of Maharashtra',
    location: 'Pune, Maharashtra',
    year: '2024',
    value: '₹2.5 Cr',
    description: 'Complete smart classroom setup with interactive displays, digital learning platforms, and e-learning infrastructure.',
    scope: ['50+ Smart Classrooms', 'Interactive Displays', 'Content Development', 'Teacher Training'],
    gradient: 'from-blue-600 via-indigo-600 to-purple-700',
    icon: Monitor,
    stats: { classrooms: '50+', students: '5,000+', teachers: '200+' }
  },
  {
    id: 2,
    title: 'Medical College Infrastructure - Mumbai',
    category: 'healthcare',
    client: 'Medical College Mumbai',
    location: 'Mumbai, Maharashtra',
    year: '2024',
    value: '₹5.8 Cr',
    description: 'Comprehensive healthcare infrastructure including medical equipment, hospital furniture, and central kitchen setup.',
    scope: ['Hospital Furniture', 'Medical Equipment', 'Central Kitchen', 'Staff Training'],
    gradient: 'from-red-500 via-rose-600 to-pink-600',
    icon: Stethoscope,
    stats: { beds: '500+', departments: '12', staff: '1,000+' }
  },
  {
    id: 3,
    title: 'Digital Transformation - District Office',
    category: 'technology',
    client: 'District Collector Office',
    location: 'Nagpur, Maharashtra',
    year: '2023',
    value: '₹1.2 Cr',
    description: 'Complete digitization of office operations with custom software, document management, and workflow automation.',
    scope: ['Custom Software', 'Document Digitization', 'ERP Implementation', 'Training'],
    gradient: 'from-emerald-500 via-teal-600 to-cyan-600',
    icon: Wifi,
    stats: { users: '500+', documents: '1 Lakh+', processes: '50+' }
  },
  {
    id: 4,
    title: 'School Infrastructure Development - Rural',
    category: 'infrastructure',
    client: 'Zilla Parishad',
    location: 'Multiple Districts',
    year: '2023',
    value: '₹8.5 Cr',
    description: 'Complete infrastructure development for 100+ rural schools including furniture, playgrounds, and sanitation.',
    scope: ['100+ Schools', 'Furniture Supply', 'Playground Development', 'Sanitation'],
    gradient: 'from-amber-500 via-orange-600 to-red-600',
    icon: GraduationCap,
    stats: { schools: '100+', villages: '50+', students: '25,000+' }
  },
  {
    id: 5,
    title: 'CCTV Surveillance - Smart City Project',
    category: 'government',
    client: 'Smart City Corporation',
    location: 'Nashik, Maharashtra',
    year: '2024',
    value: '₹4.2 Cr',
    description: 'City-wide surveillance system with 500+ cameras, monitoring center, and integrated security platform.',
    scope: ['500+ Cameras', 'Control Room Setup', 'Monitoring Platform', '24/7 Support'],
    gradient: 'from-slate-600 via-gray-700 to-zinc-800',
    icon: Shield,
    stats: { cameras: '500+', locations: '100+', coverage: '50 km²' }
  },
  {
    id: 6,
    title: 'University Digital Learning Platform',
    category: 'education',
    client: 'State University',
    location: 'Pune, Maharashtra',
    year: '2023',
    value: '₹3.5 Cr',
    description: 'University-wide digital learning platform with virtual classrooms, content management, and student portal.',
    scope: ['Virtual Classrooms', 'LMS Platform', 'Student Portal', 'Content Library'],
    gradient: 'from-violet-600 via-purple-600 to-fuchsia-600',
    icon: Users,
    stats: { students: '50,000+', courses: '500+', faculties: '200+' }
  },
  {
    id: 7,
    title: 'District Hospital Equipment Supply',
    category: 'healthcare',
    client: 'District Hospital',
    location: 'Aurangabad, Maharashtra',
    year: '2024',
    value: '₹3.2 Cr',
    description: 'Complete medical equipment supply and installation for district hospital with modern healthcare facilities.',
    scope: ['ICU Equipment', 'Operation Theaters', 'Diagnostic Machines', 'Training'],
    gradient: 'from-red-600 via-rose-700 to-pink-700',
    icon: Heart,
    stats: { equipment: '200+', departments: '8', capacity: '300 beds' }
  },
  {
    id: 8,
    title: 'Police Station Modernization',
    category: 'government',
    client: 'State Police Department',
    location: 'Multiple Locations',
    year: '2023',
    value: '₹2.8 Cr',
    description: 'Modern IT infrastructure and security systems for 25+ police stations with centralized monitoring.',
    scope: ['IT Infrastructure', 'CCTV Systems', 'Communication', 'Training'],
    gradient: 'from-sky-600 via-blue-700 to-indigo-800',
    icon: Shield,
    stats: { stations: '25+', personnel: '500+', systems: '100+' }
  },
  {
    id: 9,
    title: 'Corporate Office Setup - IT Park',
    category: 'infrastructure',
    client: 'Tech Park Authority',
    location: 'Pune, Maharashtra',
    year: '2024',
    value: '₹6.5 Cr',
    description: 'Complete office infrastructure for IT park including furniture, networking, and security systems.',
    scope: ['Office Furniture', 'Networking', 'Security Systems', 'Cafeteria'],
    gradient: 'from-cyan-500 via-blue-600 to-indigo-600',
    icon: Building2,
    stats: { offices: '50+', employees: '5,000+', area: '2 Lakh sq ft' }
  },
]

export default function Projects() {
  const [activeCategory, setActiveCategory] = useState('all')

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(p => p.category === activeCategory)

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/50 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Building2 className="w-4 h-4 text-primary-400" />
              <span className="text-sm text-graphite-300">Project Portfolio</span>
            </div>
            <h1 className="heading-xl text-white mb-6">
              Our <span className="gradient-text">Projects</span>
            </h1>
            <p className="text-xl text-graphite-300">
              Explore our portfolio of successful projects across government, education,
              healthcare, and infrastructure sectors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-gradient-to-r from-primary-900/50 via-secondary-900/50 to-accent-900/50 border-y border-white/5">
        <div className="container-premium">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-white">100+</div>
              <div className="text-sm text-graphite-400">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">₹150+ Cr</div>
              <div className="text-sm text-graphite-400">Total Value</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">50+</div>
              <div className="text-sm text-graphite-400">Districts Covered</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">500+</div>
              <div className="text-sm text-graphite-400">Happy Clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-y border-white/5">
        <div className="container-premium">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setActiveCategory(cat.id)}
                className="flex items-center gap-2"
              >
                <cat.icon className="w-4 h-4" />
                {cat.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => {
              const IconComponent = project.icon
              return (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Card variant="hover" className="h-full overflow-hidden cursor-pointer">
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      {/* Gradient Background */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient}`} />

                      {/* Pattern Overlay */}
                      <div className="absolute inset-0 opacity-10">
                        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <pattern id={`grid-${project.id}`} width="40" height="40" patternUnits="userSpaceOnUse">
                              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
                            </pattern>
                          </defs>
                          <rect width="100%" height="100%" fill={`url(#grid-${project.id})`} />
                        </svg>
                      </div>

                      {/* Content */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-white">
                        <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                          <IconComponent className="w-10 h-10" />
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold mb-1">{project.value}</div>
                          <div className="text-sm opacity-80">Project Value</div>
                        </div>
                      </div>

                      {/* Badges */}
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm text-white text-xs font-medium">
                          {categories.find(c => c.id === project.category)?.name}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
                          {project.year}
                        </span>
                      </div>

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <span className="text-white font-medium flex items-center gap-2">
                          View Details
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary-400 transition-colors line-clamp-2">
                        {project.title}
                      </h3>
                      <p className="text-graphite-400 text-sm mb-4 line-clamp-2">
                        {project.description}
                      </p>

                      <div className="flex items-center gap-4 text-xs text-graphite-500 mb-4">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span>{project.location}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{project.year}</span>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-2 mb-4 py-3 border-t border-b border-white/10">
                        {Object.entries(project.stats).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <div className="text-sm font-semibold text-white">{value}</div>
                            <div className="text-xs text-graphite-500 capitalize">{key}</div>
                          </div>
                        ))}
                      </div>

                      {/* Scope Tags */}
                      <div className="flex flex-wrap gap-2">
                        {project.scope.slice(0, 3).map((item, i) => (
                          <span key={i} className="px-2 py-1 rounded-lg bg-white/5 text-xs text-graphite-300">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-premium">
          <Card variant="premium" className="p-12 text-center">
            <h2 className="heading-md text-white mb-4">Have a Project in Mind?</h2>
            <p className="text-graphite-400 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help transform your institution with our comprehensive solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="gold" size="lg" asChild>
                <Link to="/contact">
                  Discuss Your Project
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/services">
                  View Services
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </section>
    </div>
  )
}